import React, { useState, useEffect } from 'react';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { getFirestore, collection, query, where, getDocs, doc, updateDoc } from 'firebase/firestore';
import { storage } from '../firebase.js';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { QRCodeSVG } from 'qrcode.react';
import './MesInformations.css';

interface MembreInfo {
  photo: string;
  loge: string;
  loges?: string[]; // ⭐ AJOUT du tableau de loges
  nom: string;
  prenom: string;
  adresse: string;
  codePostal: string;
  ville: string;
  mail: string;
  telephone: string;
  statut: string;
  grades: string;
  dateNaissance: string;
  id?: string;
}

interface EditableField {
  fieldName: keyof MembreInfo;
  value: string;
}

function MesInformations() {
  const [membreInfo, setMembreInfo] = useState<MembreInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editingField, setEditingField] = useState<EditableField | null>(null);
  const [photoFile, setPhotoFile] = useState<File | null>(null);
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  const [isEditingPhoto, setIsEditingPhoto] = useState(false);
  const [membreId, setMembreId] = useState<string>('');

  const membreUrl = membreId ? `https://glmri-6182e.web.app/verif-membre/${membreId}` : '';

  const formatDate = (dateString: string): string => {
    try {
      if (!dateString) return '';
      const [year, month, day] = dateString.split('-');
      return `${day}/${month}/${year}`;
    } catch (error) {
      console.error('Erreur de formatage de date:', error);
      return dateString;
    }
  };

  useEffect(() => {
    const auth = getAuth();
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (!user) {
        setError('Utilisateur non connecté');
        setLoading(false);
        return;
      }

      try {
        const db = getFirestore();
        const membresRef = collection(db, 'Membres');
        const q = query(membresRef, where('mail', '==', user.email));
        const querySnapshot = await getDocs(q);

        if (!querySnapshot.empty) {
          const docId = querySnapshot.docs[0].id;
          const membreData = querySnapshot.docs[0].data() as MembreInfo;
          membreData.id = docId;
          setMembreId(docId);
          
          // ⭐ Convertir l'ancien format vers le nouveau si nécessaire
          if (!membreData.loges && membreData.loge) {
            membreData.loges = [membreData.loge];
          }
          
          setMembreInfo(membreData);
          setError('');
        } else {
          setError('Membre non trouvé');
        }
      } catch (err) {
        console.error('Erreur complète:', err);
        setError('Erreur lors de la récupération des informations');
      } finally {
        setLoading(false);
      }
    });

    return () => unsubscribe();
  }, []);

  const getStoragePathFromURL = (url: string) => {
    try {
      const regex = /firebasestorage\.googleapis\.com\/v0\/b\/[^/]+\/o\/([^?]+)/;
      const match = url.match(regex);
      if (match && match[1]) {
        const path = decodeURIComponent(match[1]);
        console.log('Chemin extrait:', path);
        return path;
      }
      return null;
    } catch (error) {
      console.error('Erreur lors de l\'extraction du chemin:', error);
      return null;
    }
  };

  const handleEdit = (fieldName: keyof MembreInfo) => {
    if (membreInfo) {
      setEditingField({
        fieldName,
        value: membreInfo[fieldName] as string
      });
    }
  };

  const handleSave = async () => {
    if (!membreInfo || !editingField) return;

    try {
      const auth = getAuth();
      const user = auth.currentUser;
      
      if (!user) {
        setError('Utilisateur non connecté');
        return;
      }

      const db = getFirestore();
      const membresRef = collection(db, 'Membres');
      const q = query(membresRef, where('mail', '==', user.email));
      const querySnapshot = await getDocs(q);

      if (!querySnapshot.empty) {
        const docRef = doc(db, 'Membres', querySnapshot.docs[0].id);
        await updateDoc(docRef, {
          [editingField.fieldName]: editingField.value
        });

        setMembreInfo({
          ...membreInfo,
          [editingField.fieldName]: editingField.value
        });
        setEditingField(null);
      }
    } catch (err) {
      console.error('Erreur lors de la sauvegarde:', err);
      setError('Erreur lors de la sauvegarde des modifications');
    }
  };

  const handlePhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setPhotoFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setPhotoPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const uploadPhoto = async () => {
    if (!photoFile || !membreInfo) return;

    try {
      const auth = getAuth();
      const user = auth.currentUser;
      
      if (!user) {
        setError('Utilisateur non connecté');
        return;
      }

      if (membreInfo.photo) {
        const oldPhotoPath = getStoragePathFromURL(membreInfo.photo);
        if (oldPhotoPath) {
          try {
            const oldPhotoRef = ref(storage, oldPhotoPath);
            await deleteObject(oldPhotoRef);
          } catch (deleteError) {
            console.error('Erreur lors de la suppression de l\'ancienne photo:', deleteError);
          }
        }
      }

      const timestamp = Date.now();
      const photoRef = ref(storage, `profile_photos/${user.uid}/${timestamp}_${photoFile.name}`);
      
      const snapshot = await uploadBytes(photoRef, photoFile);
      const downloadURL = await getDownloadURL(snapshot.ref);

      const db = getFirestore();
      const membresRef = collection(db, 'Membres');
      const q = query(membresRef, where('mail', '==', user.email));
      const querySnapshot = await getDocs(q);

      if (!querySnapshot.empty) {
        const docRef = doc(db, 'Membres', querySnapshot.docs[0].id);
        await updateDoc(docRef, {
          photo: downloadURL
        });

        setMembreInfo({
          ...membreInfo,
          photo: downloadURL
        });

        setPhotoFile(null);
        setPhotoPreview(null);
        setIsEditingPhoto(false);
      }
    } catch (err) {
      console.error('Erreur lors du téléchargement de la photo:', err);
      setError('Erreur lors du téléchargement de la photo');
    }
  };

  const handleCancel = () => {
    setEditingField(null);
  };

  const cancelPhotoEdit = () => {
    setIsEditingPhoto(false);
    setPhotoFile(null);
    setPhotoPreview(null);
  };

  const renderEditableField = (label: string, fieldName: keyof MembreInfo) => {
    const isEditing = editingField?.fieldName === fieldName;

    return (
      <div className="info-group">
        <div className="label-container">
          <label>{label}:</label>
          {!isEditing && (
            <button 
              onClick={() => handleEdit(fieldName)}
              className="edit-button"
            >
              Modifier
            </button>
          )}
        </div>
        {isEditing ? (
          <div className="edit-field-container">
            <input
              type={fieldName === 'dateNaissance' ? 'date' : 'text'}
              value={editingField.value}
              onChange={(e) => setEditingField({
                ...editingField,
                value: e.target.value
              })}
              className="edit-input"
            />
            <button onClick={handleSave} className="save-button">Enregistrer</button>
            <button onClick={handleCancel} className="cancel-button">Annuler</button>
          </div>
        ) : (
          <span>
            {fieldName === 'dateNaissance' 
              ? formatDate(membreInfo?.[fieldName] as string || '')
              : membreInfo?.[fieldName] as string}
          </span>
        )}
      </div>
    );
  };

  if (loading) return <div>Chargement...</div>;
  if (error) return <div className="error-message">{error}</div>;
  if (!membreInfo) return <div>Aucune information disponible</div>;

  return (
    <div className="mes-informations-container">
      <h1>Mes Informations</h1>
      
      <div className="info-card">
        <div className="photo-qrcode-section">
          <div className="photo-section">
            {photoPreview ? (
              <img src={photoPreview} alt="Aperçu" className="profile-photo" />
            ) : membreInfo.photo ? (
              <img src={membreInfo.photo} alt="Membre" className="profile-photo" />
            ) : (
              <div className="no-photo">Aucune photo</div>
            )}
            
            <div className="photo-controls">
              {!isEditingPhoto ? (
                <button 
                  onClick={() => setIsEditingPhoto(true)} 
                  className="edit-button"
                >
                  Modifier photo
                </button>
              ) : (
                <div className="photo-edit-actions">
                  <input 
                    type="file" 
                    accept="image/*" 
                    onChange={handlePhotoChange} 
                    id="photo-upload"
                    className="file-input"
                  />
                  <label htmlFor="photo-upload" className="upload-button">
                    Choisir une photo
                  </label>
                  
                  {photoFile && (
                    <button onClick={uploadPhoto} className="save-button">
                      Enregistrer
                    </button>
                  )}
                  <button onClick={cancelPhotoEdit} className="cancel-button">
                    Annuler
                  </button>
                </div>
              )}
            </div>
          </div>
          
          <div className="qrcode-section">
            <div className="qrcode-container">
              <QRCodeSVG 
                value={membreUrl} 
                size={200}
                level="M"
                includeMargin={true}
                bgColor={"#FFFFFF"}
                fgColor={"#000000"}
              />
              <p><a href={membreUrl} target="_blank" rel="noopener noreferrer">Tester le lien</a></p>
            </div>
            <p className="qrcode-info">Scannez ce QR code pour vérifier votre statut de membre</p>
          </div>
        </div>

        <div className="info-grid">
          {renderEditableField('Nom', 'nom')}
          {renderEditableField('Prénom', 'prenom')}
          {renderEditableField('Date de Naissance', 'dateNaissance')}

          {/* ⭐ AFFICHAGE DES LOGES (MULTI-LOGES) */}
          <div className="info-group">
            <div className="label-container">
              <label>{membreInfo.loges && membreInfo.loges.length > 1 ? 'Loges:' : 'Loge:'}</label>
            </div>
            <span>
              {membreInfo.loges && membreInfo.loges.length > 0 
                ? membreInfo.loges.join(', ')
                : membreInfo.loge
              }
            </span>
          </div>

          <div className="info-group">
            <div className="label-container">
              <label>Grade:</label>
            </div>
            <span>{membreInfo.grades}</span>
          </div>

          <div className="info-group">
            <div className="label-container">
              <label>Statut:</label>
            </div>
            <span className={`statut-badge ${membreInfo.statut === 'Valide' ? 'valide' : 'non-valide'}`}>
              {membreInfo.statut}
            </span>
          </div>

          {renderEditableField('Adresse', 'adresse')}
          {renderEditableField('Code Postal', 'codePostal')}
          {renderEditableField('Ville', 'ville')}

          <div className="info-group">
            <div className="label-container">
              <label>Email:</label>
            </div>
            <span>{membreInfo.mail}</span>
          </div>

          {renderEditableField('Téléphone', 'telephone')}
        </div>
      </div>
    </div>
  );
}

export default MesInformations;